/**
 * Header — mode sticky intelligent.
 *
 * Le header se masque quand on descend dans la page et réapparaît dès qu'on
 * remonte. Le masquage se fait par transform (translation) : la hauteur
 * occupée dans la page ne change jamais, donc zéro décalage de mise en page.
 *
 * Publie aussi la hauteur du header dans la variable CSS --header-height
 * (utilisée par le méga-menu et le drawer pour se positionner).
 */
class HeaderComponent extends HTMLElement {
  #lastScrollY = 0;
  #resizeObserver = null;
  #onScroll = null;
  #setScrolled = null;

  connectedCallback() {
    this.#resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    });
    this.#resizeObserver.observe(this);

    // Mode overlay (accueil + fiche) : transparent tout en haut, opaque dès qu'on
    // défile (>20 px). Indépendant du sticky pour garder la lisibilité au scroll.
    if (this.classList.contains('header--overlay')) {
      this.#setScrolled = () => this.classList.toggle('is-scrolled', window.scrollY > 20);
      this.#setScrolled();
      window.addEventListener('scroll', this.#setScrolled, { passive: true });
    }

    if (!this.hasAttribute('data-sticky')) return;

    this.#lastScrollY = window.scrollY;
    this.#onScroll = () => this.#update();
    window.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
    if (this.#setScrolled) window.removeEventListener('scroll', this.#setScrolled);
  }

  #update() {
    const y = window.scrollY;
    const delta = y - this.#lastScrollY;

    // Seuil de 8 px pour ignorer les micro-défilements (rebond iOS notamment)
    if (Math.abs(delta) < 8) return;

    const menuOpen = this.querySelector('[aria-expanded="true"], details[open]') !== null;
    const shouldHide = delta > 0 && y > this.offsetHeight * 2 && !menuOpen && !this.matches(':focus-within');

    this.classList.toggle('header--hidden', shouldHide);
    this.#lastScrollY = y;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
